import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { returnCalendarForCurrency } from "./calendarBusinessRule";

// LQC-2674 - what a swap leg defaults to, per currency.
//
// These are measured, not invented: the most-used values in GET /Securities/swaps
// (2026-09-18). That endpoint is a securities MASTER, not a trade blotter - about 1600 of
// its 3171 rows are curve instruments - but restricting to the 1566 rows named with
// explicit dates gives the same answer on every field. Business day is "Modified
// following" in every currency.
//
// NOTE these disagree with the spec's convention table (BUS-715) for USD, GBP and AUD
// frequencies and for the EUR / AUD reference rates. Unresolved - see the working plan.
//
// The reference rates fell out the same way, which is why USD, GBP and CAD default to
// the modern RFRs and EUR stays on EURIBOR: that is what the data holds, not a policy
// choice. CHF, NZD and JPY have no local usage and take the obvious RFR from the seeded
// ReferenceRate table instead.
//
// THE CURRENCY LIST IS DELIBERATE. ValidateReferenceRate tests membership OUTSIDE its
// ContainsKey guard, so a currency with no seeded ReferenceRate rows rejects EVERY
// reference rate - and ValidateSwapReferenceRate requires a floating leg to carry one.
// A floating leg in such a currency therefore cannot be sent at all. Rates are seeded
// for AUD CAD CHF EUR GBP JPY NZD SGD USD only, so SEK is excluded despite having a
// calendar, and SGD is excluded for want of one. Check both tables before adding any.

export interface ISwapCurrencyConvention {
  fixedDayCount: string;
  floatingDayCount: string;
  fixedFrequency: string;
  floatingFrequency: string;
  referenceRate: string;
}

export const DEFAULT_BUSINESS_DAY = "Modified following";
// 2950 of 3171 real swaps are cleared.
export const DEFAULT_CLEARED = true;
export const DEFAULT_CLEARING_HOUSE = "LCH";

const CONVENTIONS: Record<string, ISwapCurrencyConvention> = {
  EUR: {
    fixedDayCount: "30/360",
    floatingDayCount: "Act/360",
    fixedFrequency: "1Y",
    floatingFrequency: "6M",
    referenceRate: "EUR-EURIBOR-6M",
  },
  GBP: {
    fixedDayCount: "Act/365",
    floatingDayCount: "Act/365",
    fixedFrequency: "1Y",
    floatingFrequency: "1Y",
    referenceRate: "GBP-SONIA",
  },
  USD: {
    fixedDayCount: "Act/360",
    floatingDayCount: "Act/360",
    fixedFrequency: "1Y",
    floatingFrequency: "1Y",
    referenceRate: "USD-SOFR",
  },
  AUD: {
    fixedDayCount: "Act/365",
    floatingDayCount: "Act/365",
    fixedFrequency: "3M",
    floatingFrequency: "3M",
    referenceRate: "AUD-BBSW-3M",
  },
  CAD: {
    fixedDayCount: "Act/365",
    floatingDayCount: "Act/365",
    fixedFrequency: "6M",
    floatingFrequency: "6M",
    referenceRate: "CAD-CORRA",
  },
  JPY: {
    fixedDayCount: "Act/365",
    floatingDayCount: "Act/365",
    fixedFrequency: "1Y",
    floatingFrequency: "6M",
    referenceRate: "JPY-TONA",
  },
  NZD: {
    fixedDayCount: "Act/365",
    floatingDayCount: "Act/365",
    fixedFrequency: "3M",
    floatingFrequency: "3M",
    referenceRate: "NZD-BKBM-3M",
  },
  CHF: {
    fixedDayCount: "30/360",
    floatingDayCount: "Act/360",
    fixedFrequency: "1Y",
    floatingFrequency: "6M",
    referenceRate: "CHF-SARON",
  },
};

// EUR is the fallback because it is over half the book, so an unmapped currency still
// opens on something sane rather than blank.
const FALLBACK = CONVENTIONS.EUR;

export function returnSwapConvention(
  currency: string | undefined | null,
): ISwapCurrencyConvention {
  if (!currency) return FALLBACK;
  return CONVENTIONS[currency.trim().toUpperCase()] ?? FALLBACK;
}

export function isCurrencyMappedToSwapConvention(
  currency: string | undefined | null,
): boolean {
  if (!currency) return false;
  return currency.trim().toUpperCase() in CONVENTIONS;
}

export const SWAP_CONVENTION_CURRENCIES: string[] = Object.keys(CONVENTIONS);

export interface ISwapLegDefaults {
  couponFrequency: string;
  resetFrequency: string;
  referenceRate: string;
  dayCount: string;
  businessDay: string;
  calendar: string;
}

// A fixed leg carries no reference rate and no reset frequency - ValidateSwapReferenceRate
// rejects a rate on a fixed leg outright.
export function returnSwapLegDefaults(
  currency: string,
  interestType: TradeInterestType,
): ISwapLegDefaults {
  const convention = returnSwapConvention(currency);
  const isFixed = interestType === TradeInterestType.Fixed;
  // The EUR fallback's rate would be rejected for any other currency, so leave it blank.
  const referenceRate = isCurrencyMappedToSwapConvention(currency)
    ? convention.referenceRate
    : "";
  return {
    couponFrequency: isFixed
      ? convention.fixedFrequency
      : convention.floatingFrequency,
    resetFrequency: isFixed ? "" : convention.floatingFrequency,
    referenceRate: isFixed ? "" : referenceRate,
    dayCount: isFixed ? convention.fixedDayCount : convention.floatingDayCount,
    businessDay: DEFAULT_BUSINESS_DAY,
    calendar: returnCalendarForCurrency(currency),
  };
}

// Spec F10: delivery Physical and option cleared as Swap (true), though local data is
// mostly Cash / uncleared. The spec gives no underlying default; it stays uncleared.
export const SWAPTION_DEFAULT_CLEARED = true;
export const SWAPTION_DEFAULT_UNDERLYING_CLEARED = false;
